import {AfterViewInit, Component, Inject, OnDestroy, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CurrencyPipe, DatePipe, PercentPipe} from '@angular/common';
import {MatAccordion, MatExpansionPanel} from '@angular/material/expansion';
import {of, combineLatest, Subject} from 'rxjs';
import {catchError, filter, finalize, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {OfferService} from '../../../../services/offer/offer.service';
import {OfferVersionHistoryEntry, VersionFieldChange} from '../../../../model/offer/offer-version-history-entry';
import {Offer} from '../../../../model/offer/offer';
import {PermilleFormatPipe} from '../../../../pipes/permille-format';

interface ParsedChange {
  dto: string;
  entityId: string | null;
  property: string;
  oldValue: any;
  newValue: any;
}

interface DisplayRow {
  label: string;
  formattedNew: string;
  formattedOld: string;
  hasOld: boolean;
}

export interface OfferVersionHistoryDialogData {
  offerId: string;
}

const FIELD_LABEL_KEYS: Record<string, Record<string, string>> = {
  OfferDto: {
    premiumInPercent:             'offer.inPercent',
    premiumPerThousand:           'offer.perThousand',
    premium:                      'offer.premium',
    expectedTotalPremium:         'offer.expectedTotalPremiumWithoutSupercript',
    advancePremium:               'offer.advancePremium',
    minimumPremium:               'offer.minimumPremium',
    typeOfDeductible:             'proposalDetails.createOfferTab.typeOfDeductible',
    deductibleAbsoluteAmount:     'proposalDetails.createOfferTab.deductibleAbsoluteAmount',
    deductibleCurrency:           'offer.versionHistory.deductibleCurrency',
    referenceValue:               'offer.createOffer.referenceValue',
    currency:                     'offer.versionHistory.currency',
    amountCovered:                'offer.share',
    isLeading:                    'offer.role',
    standardTermsAndConditions:   'offer.defaultTerms',
    individualTermsAndConditions: 'offer.individualTerms',
    standardClauses:              'offer.defaultClauses',
    individualClauses:            'offer.individualClauses',
    otherInformation:             'offer.createOffer.individualInformationSection.otherInformation',
    offerDescription:             'proposalDetails.createOfferTab.premiumInformationSection.offerDescription',
    attachedFiles:                'offer.files',
  },
  ExternalInsurerDto: {
    name:        'offer.name',
    streetName:  'offer.streetAndNumber',
    houseNumber: 'offer.streetAndNumber',
    postalCode:  'offer.postalCodeAndLocation',
    location:    'offer.postalCodeAndLocation',
    countryCode: 'proposalDetails.lkz',
  },
  BinaryDataDto: {
    attachedFiles: 'offer.files',
  },
};

@Component({
  selector: 'app-offer-version-history-dialog',
  templateUrl: './offer-version-history-dialog.component.html',
  styleUrls: ['./offer-version-history-dialog.component.scss'],
  standalone: false,
  providers: [DatePipe, CurrencyPipe, PercentPipe, PermilleFormatPipe]
})
export class OfferVersionHistoryDialogComponent implements AfterViewInit, OnDestroy {

  versions: Array<{ entry: OfferVersionHistoryEntry; versionNumber: number; rows: DisplayRow[] }> = [];
  loading: boolean = false;
  errored: boolean = false;
  currentFileIds: Set<string> = new Set<string>();

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  private _allDetailsOpen = false;

  get allDetailsOpen(): boolean {
    return this._allDetailsOpen;
  }

  set allDetailsOpen(value: boolean) {
    this._allDetailsOpen = value;
    if (value) {
      this.accordion?.openAll();
    } else {
      this.accordion?.closeAll();
    }
  }

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private readonly offerService: OfferService,
    private readonly translate: TranslateService,
    private readonly datePipe: DatePipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly percentPipe: PercentPipe,
    private readonly permillePipe: PermilleFormatPipe,
    public dialogRef: MatDialogRef<OfferVersionHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OfferVersionHistoryDialogData
  ) {
    this.loadHistory();
  }

  ngAfterViewInit(): void {
    this.panels.changes
      .pipe(
        filter((value: QueryList<MatExpansionPanel>) => value.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((value: QueryList<MatExpansionPanel>) => {
        const obs = value.toArray().map(panel => panel.expandedChange);
        combineLatest(obs).pipe(takeUntil(this.destroy$)).subscribe((arr: boolean[]) => {
          if (arr.every(isOpen => isOpen)) {
            this._allDetailsOpen = true;
          } else if (arr.every(isOpen => !isOpen)) {
            this._allDetailsOpen = false;
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  close(): void {
    this.dialogRef.close();
  }

  private loadHistory(): void {
    this.loading = true;
    this.errored = false;
    this.versions = [];
    this.offerService.getOfferHistory(this.data.offerId).pipe(
      catchError(() => {
        this.errored = true;
        return of([] as OfferVersionHistoryEntry[]);
      }),
      finalize(() => this.loading = false)
    ).subscribe(history => {
      this.versions = history
        .map((entry, idx) => ({
          entry,
          versionNumber: idx + 1,
          rows: this.buildDisplayRows(entry.changes ?? [], entry.offer)
        }))
        .reverse();
      this.currentFileIds = this.computeCurrentFileIds();
    });
  }

  private computeCurrentFileIds(): Set<string> {
    const latest = this.versions[0];
    const files = latest?.entry?.offer?.attachedFiles ?? [];
    return new Set(files.map((f: any) => f?.id).filter((id: any): id is string => !!id));
  }

  private parseField(field: string, oldValue: any, newValue: any): ParsedChange | null {
    if (!field || typeof field !== 'string') return null;
    const match = /(?:\.\.\.|^|\.)([A-Z]\w+)\/([^.]*)\.(.+)$/.exec(field);
    if (!match) return null;
    const [, dto, entityIdRaw, property] = match;
    return {
      dto,
      entityId: entityIdRaw && entityIdRaw.length > 0 ? entityIdRaw : null,
      property,
      oldValue,
      newValue
    };
  }

  buildDisplayRows(rawChanges: VersionFieldChange[], offer: Offer): DisplayRow[] {
    if (!rawChanges || rawChanges.length === 0) return [];
    const rows: DisplayRow[] = [];
    for (const change of rawChanges) {
      const parsed = this.parseField(change.field, change.oldValue, change.newValue);
      if (!parsed) continue;
      rows.push(this.buildRow(parsed, offer));
    }
    return rows;
  }

  private buildRow(parsed: ParsedChange, offer: Offer): DisplayRow {
    return {
      label: this.fieldLabel(parsed.dto, parsed.property),
      formattedNew: this.formatValueForField(parsed.newValue, parsed.property, offer),
      formattedOld: this.formatValueForField(parsed.oldValue, parsed.property, offer),
      hasOld: parsed.oldValue != null && parsed.oldValue !== ''
    };
  }

  private formatValueForField(value: any, property: string, offer: Offer): string {
    if (value == null || value === '') return '—';
    if (['premium', 'expectedTotalPremium', 'advancePremium', 'minimumPremium'].includes(property)) {
      return this.currencyPipe.transform(value, offer?.currency) ?? String(value);
    }
    if (property === 'deductibleAbsoluteAmount') {
      return this.currencyPipe.transform(value, offer?.deductibleCurrency) ?? String(value);
    }
    if (['premiumInPercent', 'amountCovered'].includes(property)) {
      return this.percentPipe.transform(value, '1.2-2') ?? String(value);
    }
    if (property === 'premiumPerThousand') {
      return this.permillePipe.transform(value);
    }
    return this.formatValue(value);
  }

  private fieldLabel(dto: string, property: string): string {
    const key = FIELD_LABEL_KEYS[dto]?.[property];
    if (key) return this.translate.instant(key);
    // fallback: humanise the raw property name
    return property;
  }

  private formatValue(value: any): string {
    if (value == null || value === '') return '—';
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'proposalDetails.yes' : 'proposalDetails.no');
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return '—';
      if (value.every(v => typeof v === 'string' || typeof v === 'number')) return value.join(', ');
      return `${value.length}`;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return this.datePipe.transform(value, 'mediumDate') ?? value;
    }
    if (typeof value === 'object') {
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    return String(value);
  }

  trackByVersion(_index: number, item: { versionNumber: number }): number {
    return item.versionNumber;
  }

  trackByRow(index: number, _row: DisplayRow): number {
    return index;
  }
}
